import calculatorRegistry from "../../../../generated/calculatorRegistry.json";
import type { HelpArticle, HelpBlock } from "../../types";

type RegistryField = {
  key: string;
  label: string;
  type?: string;
  unit?: string;
  required?: boolean;
  default?: unknown;
  options?: unknown[];
  min?: number;
  max?: number;
  help?: string;
};

type RegistryGroup = {
  id: string;
  label: string;
  help?: string;
  fields?: RegistryField[];
};

type RegistryBinding = {
  id: string;
  label: string;
  catalog?: string;
  entity?: string;
  inputKey?: string;
  stableKeyName?: string;
  required?: boolean;
  readiness?: string;
  dependsOn?: string[];
};

type RegistryPreset = {
  id: string;
  name: string;
  summary?: string;
};

type RegistryCalculator = {
  id: string;
  name: string;
  subtitle?: string;
  category?: string;
  element?: string;
  material?: string;
  standard?: string;
  status?: string;
  summary?: string;
  presets?: RegistryPreset[];
  catalogBindings?: RegistryBinding[];
  inputSchema?: { groups?: RegistryGroup[] };
};

const calculators = (calculatorRegistry as { calculators: RegistryCalculator[] }).calculators;

function display(value: unknown): string {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "boolean") return value ? "On" : "Off";
  const text = Array.isArray(value) ? value.join(", ") : String(value);
  return text.length > 100 ? `${text.slice(0, 97)}…` : text;
}

function fieldShape(field: RegistryField): string {
  const shape = [field.type ?? "value"];
  if (field.unit) shape.push(field.unit);
  if (field.options?.length) shape.push(`choices: ${field.options.map(display).join(" / ")}`);
  if (field.min !== undefined || field.max !== undefined) {
    shape.push(`range ${field.min ?? "open"} to ${field.max ?? "open"}`);
  }
  return shape.join(" · ");
}

function relatedFor(calculator: RegistryCalculator): string[] {
  if (calculator.category === "loads" || calculator.element === "load_generator") {
    return ["load-calcs", "load-calc-results", "load-types"];
  }
  if (calculator.category === "connections" || /connection|hanger|hold-down/i.test(calculator.name)) {
    return ["connection-checks", "product-lookup", "linking-reactions"];
  }
  if (calculator.material === "wood") {
    return ["member-sheets", "member-results", "design-conditions"];
  }
  if (calculator.id === "section_properties") {
    return ["section-properties", "schema-sheets", "calculation-provenance"];
  }
  return ["schema-sheets", "statuses-and-verdicts", "calculation-provenance"];
}

function buildCalculatorArticle(calculator: RegistryCalculator): HelpArticle {
  const topicId = `calculator-${calculator.id.replaceAll("_", "-")}`;
  const groups = calculator.inputSchema?.groups ?? [];
  const bindings = calculator.catalogBindings ?? [];
  const presets = calculator.presets ?? [];
  const blocks: HelpBlock[] = [
    {
      kind: "lead",
      text: calculator.summary
        ?? `${calculator.name} is an available calculator in the Engineering Studio registry.`,
    },
    { kind: "where", path: ["Calculations", "New calculation", calculator.name] },
    {
      kind: "badge-row",
      badges: [
        { label: calculator.standard ?? "Standard recorded in result", variant: "accent" },
        { label: calculator.status ?? "available", variant: calculator.status === "available" ? "success" : "neutral" },
        { label: calculator.material ?? calculator.category ?? "analysis", variant: "neutral" },
      ],
    },
    { kind: "heading", level: 2, text: "What this calculator checks", id: "scope" },
    {
      kind: "paragraph",
      text: calculator.summary
        ?? `Use this sheet for the published ${calculator.name} scope. The result record states the checks the engine completed.`,
    },
    {
      kind: "callout",
      variant: "note",
      title: "Scope is part of the result",
      body: "A passing emitted check does not imply coverage for limit states, detailing, load paths, or applicability conditions the record does not publish. Review the complete record and cover omitted scope separately.",
    },
  ];

  if (presets.length > 0) {
    blocks.push(
      { kind: "heading", level: 2, text: "Starting presets", id: "presets" },
      {
        kind: "table",
        headers: ["Preset", "What it seeds"],
        rows: presets.map((preset) => [preset.name, preset.summary ?? "Editable starting values for this calculator."]),
      },
    );
  }

  if (bindings.length > 0) {
    blocks.push(
      { kind: "heading", level: 2, text: "Catalog selections", id: "catalog" },
      {
        kind: "paragraph",
        text: "These selectors resolve database records through the calculator registry. The persisted input is the stable key; verify the visible identity and source after selection.",
      },
      {
        kind: "table",
        headers: ["Selection", "Catalog record", "Stored key", "Dependency"],
        rows: bindings.map((binding) => [
          `${binding.label}${binding.required ? " (required)" : ""}`,
          [binding.catalog, binding.entity].filter(Boolean).join(" · ") || "Registry-defined",
          [binding.inputKey, binding.stableKeyName].filter(Boolean).join(" → ") || binding.id,
          binding.dependsOn?.length ? binding.dependsOn.join(", ") : "None",
        ]),
      },
    );
  }

  blocks.push({ kind: "heading", level: 2, text: "Inputs", id: "inputs" });
  if (groups.length === 0) {
    blocks.push(
      {
        kind: "paragraph",
        text: "This load/analysis calculator uses its dedicated workspace instead of the registry-generated field groups used by structural sheets. Follow the visible workflow in order and keep every site, geometry, assembly, coefficient, and load value in the units shown.",
      },
    );
  } else {
    for (const group of groups) {
      blocks.push({ kind: "heading", level: 3, text: group.label, id: `inputs-${group.id}` });
      if (group.help) {
        blocks.push({ kind: "registry-group", calculatorId: calculator.id, groupId: group.id });
      }
      const fields = group.fields ?? [];
      blocks.push({
        kind: "table",
        headers: ["Input", "Type / units", "Default", "Requirement"],
        rows: fields.map((field) => [
          `${field.label} · ${field.key}`,
          fieldShape(field),
          display(field.default),
          field.required ? "Required" : "Optional",
        ]),
      });
      for (const field of fields.filter((entry) => Boolean(entry.help))) {
        blocks.push({ kind: "registry-field", calculatorId: calculator.id, fieldKey: field.key });
      }
    }
  }

  blocks.push(
    { kind: "heading", level: 2, text: "Run and read the result", id: "result" },
    {
      kind: "steps",
      steps: [
        { title: "Resolve identity first", body: "Complete required catalog/product selections and confirm the calculator, standard, material, and design method shown by the sheet." },
        { title: "Commit the inputs", body: "Finish text/number fields with blur or `Enter`; selectors commit on change; **Recalculate** commits anything still pending." },
        { title: "Read every emitted check", body: "Trace governing demand, capacity or derived value, ratio/status where applicable, units, combination/case, references, warnings, and sources." },
        { title: "Record omitted scope", body: "Cover required checks or detailing conditions that are outside the published result by another documented method." },
      ],
    },
    { kind: "heading", level: 2, text: "Preflight", id: "preflight" },
    {
      kind: "list",
      style: "unordered",
      items: [
        "Calculator and structural idealization match the condition being represented.",
        "Units, signs, directions, support/restraint assumptions, and design method are explicit.",
        "Catalog identities and manual values are sourced and still resolve after reload.",
        "The current result has no ignored refusal, stale dependency, missing check, warning, or applicability gap.",
      ],
    },
    {
      kind: "faq",
      items: [
        {
          question: `Which standard does ${calculator.name} report?`,
          answer: calculator.standard
            ? `The registry identifies **${calculator.standard}**. Read the generated record for the exact clauses, procedures, and limitations applied to this run.`
            : "The result record identifies the procedure and sources used by the current adapter. Do not infer an edition from the calculator title alone.",
        },
        {
          question: "Why does a field's Help wording match the sheet?",
          answer: "Both surfaces read the same vendored calculator registry. The Help index gate rejects a field or group transclusion that no longer resolves, preventing a stale copy from shipping.",
        },
      ],
    },
    { kind: "related", topics: relatedFor(calculator) },
  );

  return {
    id: topicId,
    title: calculator.name,
    description: `${calculator.standard ?? "Registry-backed"} reference: scope, catalog bindings, every current input group, and result preflight.`,
    tags: [calculator.name, calculator.id, calculator.standard ?? "calculator", calculator.category ?? "reference"],
    lastUpdated: "July 20, 2026",
    published: true,
    blocks,
  };
}

export const calculatorReferenceArticles: Record<string, HelpArticle> = Object.fromEntries(
  calculators.map((calculator) => {
    const article = buildCalculatorArticle(calculator);
    return [article.id, article];
  }),
);
